import { useState } from 'react';
import { P, Section } from '@vp/swan';
import { scrollToElementById } from '@99designs/browser';
import { BIFROST_ENABLED_LOCALES } from '@99designs/design-services-common';
import { useIsFeatureFlagActive } from '@99designs/experiments';
import { SupportedLocale, __ } from '@99designs/i18n';
import { Recommendations, isPlacementLocaleSupported } from '@99designs/recommendations';
import { TrustpilotWidget, isTrustpilotEnabledLocale } from '@99designs/reviews';
import { DesignCategories } from '../DesignCategories';
import { FAQ } from '../FAQ';
import { Header } from '../Header';
import { HowItWorks } from '../HowItWorks';
import { LogoBanner } from '../LogoBanner';
import { StickyNav } from '../StickyNav';
import { SupportBanner } from '../SupportBanner';
import { WhyHireOnVistaprint } from '../WhyHireOnVistaprint';
import type { ProductGroups } from '../../types';

interface HubMainContentProps {
    selectedCategoryId?: string;
    locale: SupportedLocale;
    productGroups: ProductGroups;
}

const RecommendationsSectionId = 'recommendations';
const DesignCategoriesSectionId = 'design-categories-section';
const WhyHireOnVistaprintSectionId = 'why-hire-on-vistaprint-section';
const HowItWorksSectionId = 'how-it-works-section';
const FAQSectionId = 'faq-section';
const ScrollTopMargin = 75;

export const HubMainContent = ({
    selectedCategoryId,
    locale,
    productGroups,
}: HubMainContentProps) => {
    const [error, setError] = useState('');
    const showRecommendations = isPlacementLocaleSupported('recently-viewed', locale);
    const showRecommendationsNavItem = showRecommendations && error === '';
    const recommendationsNavItem = {
        label: __('Recommended', {
            _context: 'Navigation link to the section displaying recommended design services',
        }),
        href: `#recommendations`,
    };

    let bannerBelowHowItWorks: JSX.Element | null = null;
    if (BIFROST_ENABLED_LOCALES.includes(locale)) {
        bannerBelowHowItWorks = (
            <Section marginBottom="between-sections" style={{ boxShadow: 'none' }}>
                <LogoBanner locale={locale} />
            </Section>
        );
    }
    const isTrustpilotFeatureEnabled = useIsFeatureFlagActive(
        'design_services_hub_page_trust_signal'
    );

    return (
        <>
            <Section data-testid="hub-hero-section">
                <Header
                    browseDesignServicesCtaOnClick={() => {
                        scrollToElementById(DesignCategoriesSectionId, ScrollTopMargin);
                    }}
                    howItWorksCtaOnClick={() => {
                        scrollToElementById(HowItWorksSectionId, ScrollTopMargin);
                    }}
                />
            </Section>
            <StickyNav
                navItems={[
                    ...(showRecommendationsNavItem ? [recommendationsNavItem] : []),
                    { label: __('Design services'), href: `#${DesignCategoriesSectionId}` },
                    { label: __('Why Vistaprint?'), href: `#${WhyHireOnVistaprintSectionId}` },
                    { label: __('How it works'), href: `#${HowItWorksSectionId}` },
                    { label: __('FAQs'), href: `#${FAQSectionId}` },
                ]}
            />
            {showRecommendations && (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={RecommendationsSectionId}
                    data-testid="hub-recommendations-section"
                    style={{ boxShadow: 'none' }}
                >
                    <Recommendations onError={setError} />
                </Section>
            )}
            {productGroups.length > 0 ? (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={DesignCategoriesSectionId}
                    data-testid="hub-design-categories-section"
                    style={{ boxShadow: 'none' }}
                >
                    <DesignCategories
                        title={__('Design Services')}
                        categories={productGroups}
                        locale={locale as SupportedLocale}
                        initialCategoryId={selectedCategoryId}
                    />
                </Section>
            ) : (
                <Section
                    marginTop="between-sections"
                    marginBottom="between-sections"
                    id={DesignCategoriesSectionId}
                    data-testid="hub-design-categories-error-section"
                    style={{ boxShadow: 'none' }}
                >
                    <P textAlign="center">
                        {__(
                            'Whoops - design categories could not be loaded but we are working on it.',
                            {
                                _context:
                                    'An error message displayed on a landing page when a component containing design categories cannot be loaded correctly',
                            }
                        )}
                    </P>
                </Section>
            )}
            <Section
                marginBottom="between-sections"
                id={WhyHireOnVistaprintSectionId}
                data-testid="hub-why-vistaprint-section"
                style={{ boxShadow: 'none' }}
            >
                <WhyHireOnVistaprint />
            </Section>
            <Section
                marginBottom="between-sections"
                id={HowItWorksSectionId}
                data-testid="hub-how-it-works-section"
                style={{ boxShadow: 'none' }}
            >
                <HowItWorks locale={locale} />
            </Section>

            {bannerBelowHowItWorks}

            {isTrustpilotEnabledLocale(locale) && isTrustpilotFeatureEnabled() && (
                <Section
                    marginBottom="between-sections"
                    data-testid="hub-trustpilot-section"
                    style={{ boxShadow: 'none' }}
                >
                    <TrustpilotWidget locale={locale} />
                </Section>
            )}

            <Section
                marginBottom="between-sections"
                id={FAQSectionId}
                data-testid="hub-faq-section"
                style={{ boxShadow: 'none' }}
            >
                <FAQ />
            </Section>
            <Section data-testid="hub-support-section" style={{ boxShadow: 'none' }}>
                <SupportBanner locale={locale} />
            </Section>
        </>
    );
};
